import apiClient from '../api';
import type {
  TrainingPlanSummary, TrainingPlanDetail, TrainingAssignment,
  AthleteAssignmentDetail, SessionDetail, SessionSummary,
  TrainingDay, TrainingExercise,
  CreateTrainingPlanForm, UpdateTrainingPlanForm, UpdateAthletesPlanForm,
  CreateTrainingDayForm, UpdateTrainingExerciseForm,
  CreateTrainingExerciseForm, CreateAssignmentForm, LogSessionForm, UpdateSessionForm,
  AthleteTrainingActivity,
} from '../../types/training';

const BASE = '/training';

// ── Coach: Plans ──────────────────────────────────────────────────────────────
export const getMyPlans = (): Promise<TrainingPlanSummary[]> =>
  apiClient.get(`${BASE}/plans`).then(r => r.data);

export const getPlan = (planId: string): Promise<TrainingPlanDetail> =>
  apiClient.get(`${BASE}/plans/${planId}`).then(r => r.data);

export const createPlan = (data: CreateTrainingPlanForm): Promise<TrainingPlanDetail> =>
  apiClient.post(`${BASE}/plans`, data).then(r => r.data);

export const updatePlan = (planId: string, data: UpdateTrainingPlanForm): Promise<TrainingPlanDetail> =>
  apiClient.put(`${BASE}/plans/${planId}`, data).then(r => r.data);

/** Update an athlete's plan copy and record a change-log entry. */
export const updateAthletesPlan = (planId: string, data: UpdateAthletesPlanForm): Promise<TrainingPlanDetail> =>
  apiClient.put(`${BASE}/plans/${planId}/athlete-copy`, data).then(r => r.data);

export const archivePlan = (planId: string): Promise<void> =>
  apiClient.delete(`${BASE}/plans/${planId}`).then(() => undefined);

// ── Coach: Days ───────────────────────────────────────────────────────────────
export const addDay = (planId: string, data: CreateTrainingDayForm): Promise<TrainingDay> =>
  apiClient.post(`${BASE}/plans/${planId}/days`, data).then(r => r.data);

export const updateDay = (planId: string, dayId: string, data: UpdateTrainingExerciseForm): Promise<TrainingDay> =>
  apiClient.put(`${BASE}/plans/${planId}/days/${dayId}`, data).then(r => r.data);

export const deleteDay = (planId: string, dayId: string): Promise<void> =>
  apiClient.delete(`${BASE}/plans/${planId}/days/${dayId}`).then(() => undefined);

// ── Coach: Exercises ──────────────────────────────────────────────────────────
export const addExercise = (planId: string, dayId: string, data: CreateTrainingExerciseForm): Promise<TrainingExercise> =>
  apiClient.post(`${BASE}/plans/${planId}/days/${dayId}/exercises`, data).then(r => r.data);

export const updateExercise = (planId: string, dayId: string, exerciseId: string, data: UpdateTrainingExerciseForm): Promise<TrainingExercise> =>
  apiClient.put(`${BASE}/plans/${planId}/days/${dayId}/exercises/${exerciseId}`, data).then(r => r.data);

export const deleteExercise = (planId: string, dayId: string, exerciseId: string): Promise<void> =>
  apiClient.delete(`${BASE}/plans/${planId}/days/${dayId}/exercises/${exerciseId}`).then(() => undefined);

/** Batch-reorder exercises within a day. items is ordered array of { exerciseId, sortOrder }. */
export const reorderExercises = (
  planId: string,
  dayId: string,
  items: Array<{ exerciseId: string; sortOrder: number }>
): Promise<void> =>
  apiClient.patch(`${BASE}/plans/${planId}/days/${dayId}/exercises/reorder`, { items }).then(() => undefined);

/** Batch-reorder days within a plan. items is ordered array of { dayId, sortOrder }. */
export const reorderDays = (
  planId: string,
  items: Array<{ dayId: string; sortOrder: number }>
): Promise<void> =>
  apiClient.patch(`${BASE}/plans/${planId}/days/reorder`, { items }).then(() => undefined);

// ── Coach: Assignments ────────────────────────────────────────────────────────
export const getCoachAssignments = (): Promise<TrainingAssignment[]> =>
  apiClient.get(`${BASE}/assignments`).then(r => r.data);

export const createAssignment = (data: CreateAssignmentForm): Promise<TrainingAssignment> =>
  apiClient.post(`${BASE}/assignments`, data).then(r => r.data);

export const deactivateAssignment = (assignmentId: string): Promise<void> =>
  apiClient.delete(`${BASE}/assignments/${assignmentId}`).then(() => undefined);

/**
 * End a coach-assigned plan — deactivates the assignment and sends an optional
 * reason to the athlete via message notification. Session history is preserved.
 */
export const endAssignment = (assignmentId: string, reason?: string): Promise<void> =>
  apiClient.post(`${BASE}/assignments/${assignmentId}/end`, { reason: reason ?? null }).then(() => undefined);

// ── Athlete: View ─────────────────────────────────────────────────────────────
export const getAthleteAssignments = (viewAs?: string): Promise<AthleteAssignmentDetail[]> =>
  apiClient.get(`${BASE}/my-assignments`, { params: viewAs ? { viewAs } : {} }).then(r => r.data);

export const logSession = (data: LogSessionForm): Promise<SessionDetail> =>
  apiClient.post(`${BASE}/sessions`, data).then(r => r.data);

export const getSession = (sessionId: string, viewAs?: string): Promise<SessionDetail> =>
  apiClient.get(`${BASE}/sessions/${sessionId}`, { params: viewAs ? { viewAs } : {} }).then(r => r.data);

export const getSessionHistory = (viewAs?: string, page = 1, pageSize = 20): Promise<SessionSummary[]> =>
  apiClient.get(`${BASE}/sessions`, { params: { page, pageSize, ...(viewAs ? { viewAs } : {}) } }).then(r => r.data);

export const updateSession = (sessionId: string, data: UpdateSessionForm): Promise<SessionDetail> =>
  apiClient.put(`${BASE}/sessions/${sessionId}`, data).then(r => r.data);

/**
 * Get the last logged session for a specific training day.
 * Returns null if the athlete has never logged this day before.
 * Used by the log-session modal to show previous weights/reps as a reference.
 */
export const getLastSessionForDay = (
  trainingDayId: string,
  assignmentId: string,
  viewAs?: string
): Promise<SessionDetail | null> =>
  apiClient.get(`${BASE}/sessions/last-for-day`, {
    params: { trainingDayId, assignmentId, ...(viewAs ? { viewAs } : {}) },
  }).then(r => r.data ?? null);

/** Coach: get per-athlete training activity summary for the coach dashboard. */
export const getAthleteTrainingActivity = (): Promise<AthleteTrainingActivity[]> =>
  apiClient.get(`${BASE}/coach/athlete-activity`).then(r => r.data);

// ── Self-Coached Plans (Athlete, no coach required) ───────────────────────────

/**
 * Create a new training plan and immediately self-assign it.
 * Returns the new assignment detail (isSelfCoached = true).
 */
export const createSelfPlan = (data: { name: string; description?: string; notes?: string }): Promise<AthleteAssignmentDetail> =>
  apiClient.post(`${BASE}/self/plans`, data).then(r => r.data);

/**
 * Soft-archive a self-created plan and deactivate its assignment.
 * Session history is preserved.
 */
export const deleteSelfPlan = (planId: string): Promise<void> =>
  apiClient.delete(`${BASE}/self/plans/${planId}`).then(() => undefined);
